import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTruckMoving, faExclamationTriangle, faCheckCircle, faClock, faRoute, faArrowRight, faMapMarkedAlt, faFileInvoiceDollar, faBoxes, faPlaneArrival, faDollarSign, faUserAlt, faShippingFast } from '@fortawesome/free-solid-svg-icons';
import './ShipmentDetails.css';
import HereMap from './HereMap';
import logo from '../wisdom.png';

const ShipmentDetails = () => {
  const { id } = useParams();
  const location = useLocation();
  const shipment = location.state?.shipment;
  const apiKey = "UPYsmyp8W7_KCaDWBIgYhbIXl6dEsMa08kb4eiZJqs8";
  const [originWeather, setOriginWeather] = useState(null);
  const [destinationWeather, setDestinationWeather] = useState(null);
  const [eta, setEta] = useState(0);

  // Define geocodeLocation outside useEffect
  const geocodeLocation = async (addressData) => {
    const geocodeUrl = `https://geocode.search.hereapi.com/v1/geocode?q=${encodeURIComponent(addressData)}&apiKey=${apiKey}`;
    try {
      const response = await fetch(geocodeUrl);
      const data = await response.json();
      if (data.items && data.items.length > 0) {
        return data.items[0].position; // Returns {lat, lng}
      } else {
        throw new Error('Location not found');
      }
    } catch (error) {
      console.error("Geocode error:", error);
      throw error;
    }
  };

  // Define fetchWeatherInfo outside useEffect
  const fetchWeatherInfo = async (lat, lng) => {
    const weatherApiUrl = `https://weather.ls.hereapi.com/weather/1.0/report.json?apiKey=${apiKey}&product=observation&latitude=${lat}&longitude=${lng}`;
    try {
      const response = await fetch(weatherApiUrl);
      const data = await response.json();
      // Check if the response contains the expected data
      if (data.observations && data.observations.location && data.observations.location.length > 0 && data.observations.location[0].observation && data.observations.location[0].observation.length > 0) {
        return data.observations.location[0].observation[0]; // Returns the first observation
      } else {
        console.error("No weather data available", data);
        return null; // Handle cases where no data is available
      }
    } catch (error) {
      console.error("Failed to fetch weather data", error);
      return null; // Ensure function returns null in case of error
    }
  };


  // Define calculateETA outside useEffect
  const calculateETA = async (origin, destination) => {
    const platform = new window.H.service.Platform({
      'apikey': apiKey
    });
    const router = platform.getRoutingService(null, 8);
    const routingParameters = {
      'routingMode': 'fast',
      'transportMode': 'car',
      'origin': `${origin.lat},${origin.lng}`,
      'destination': `${destination.lat},${destination.lng}`,
      'return': 'summary'
    };

    return new Promise((resolve, reject) => {
      router.calculateRoute(routingParameters, (result) => {
        if (result.routes && result.routes.length) {
          // Ensure every section has a travelSummary and duration before trying to reduce
          const eta = result.routes[0].sections.reduce((total, section) => {
            if (section.travelSummary && section.travelSummary.duration !== undefined) {
              return total + section.travelSummary.duration;
            } else {
              console.warn('Section missing travelSummary or duration:', section);
              return total; // Continue accumulating without adding undefined or non-existent durations
            }
          }, 0);
          resolve(eta); // Duration in seconds
        } else {
          reject(new Error('No routes found.'));
        }
      }, (error) => {
        console.error('Routing request error:', error);
        reject(error);
      });
    });
  };


  useEffect(() => {
    if (!shipment) return;

    const consignorAddress = `${shipment.consignorCity}, ${shipment.consignorState}, ${shipment.consignorPostcode}`;
    const consigneeAddress = `${shipment.consigneeCity}, ${shipment.consigneeState}, ${shipment.consigneePostcode}`;

    const fetchData = async () => {
      try {
        const [originCoords, destinationCoords] = await Promise.all([
          geocodeLocation(consignorAddress),
          geocodeLocation(consigneeAddress),
        ]);
        /*const [originWeatherData, destinationWeatherData] = await Promise.all([
          fetchWeatherInfo(originCoords.lat, originCoords.lng),
          fetchWeatherInfo(destinationCoords.lat, destinationCoords.lng),
        ]);
        */
        const etaSeconds = await calculateETA(originCoords, destinationCoords);

        //setOriginWeather(originWeatherData);
        //setDestinationWeather(destinationWeatherData);
        setEta(etaSeconds);
      } catch (error) {
        console.error("Error fetching additional data:", error);
      }
    };

    fetchData();
  }, [shipment]); // Make sure to include dependencies here if any

  if (!shipment) {
    return <div>Loading...</div>;
  }

  // Function to determine shipment status
  const getShipmentStatus = (consolAta, destinationEta, consolAtd, originEtd) => {
    const ataDate = new Date(consolAta);
    const etaDate = new Date(destinationEta);
    const atdDate = new Date(consolAtd);
    const etdDate = new Date(originEtd);

    if (ataDate > etaDate) {
      return 'Late';
    } else if (ataDate.toISOString() === etaDate.toISOString()) {
      return 'On Time';
    } else if (atdDate > etdDate) {
      return 'At Risk';
    } else {
      return 'Status Unknown'; // Default case
    }
  };

  const shipmentStatus = getShipmentStatus(
    shipment.consolAta,
    shipment.destinationEta,
    shipment.consolAtd,
    shipment.originEtd
  );

  const calculateTransitTime = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.round((endDate - startDate) / (1000 * 60 * 60 * 24));
  };

  const transitTime = calculateTransitTime(shipment.consolAtd, shipment.consolAta);

  const formatDate = dateString => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const consignorAddress = `${shipment.consignorCity}, ${shipment.consignorState}, ${shipment.consignorPostcode}`;
  const consigneeAddress = `${shipment.consigneeCity}, ${shipment.consigneeState}, ${shipment.consigneePostcode}`;

  return (
    <>
      <nav className="w-full flex justify-between items-center px-6 py-4 bg-white shadow">
        <div className="flex items-center">
          <img src={logo} alt="Company logo" className="h-12 mr-2" />
        </div>
        <div className="text-xl font-semibold text-gray-700">
          Shipment Details
        </div>
      </nav>

      <div className="min-h-screen bg-gray-100 p-6">
        <div className="mb-4"> {/* Reduced outer margin */}
          <div className="bg-white shadow-lg rounded-lg p-4 grid grid-cols-1 md:grid-cols-5 divide-x divide-gray-200"> {/* Reduced padding */}
            <div className="flex flex-col items-center justify-center p-2"> {/* Reduced padding */}
              <FontAwesomeIcon icon={faTruckMoving} className="text-blue-500 text-3xl mb-1" /> {/* Reduced margin */}
              <span className="text-lg font-bold text-gray-800">Shipment ID</span>
              <span className="font-semibold">{shipment.shipmentId}</span>
            </div>
            <div className="flex flex-col items-center justify-center p-2"> {/* Reduced padding */}
              <FontAwesomeIcon icon={faClock} className="text-green-500 text-3xl mb-1" /> {/* Reduced margin */}
              <span className="text-lg font-bold text-gray-800">Transit Time</span>
              <span className="font-semibold">{transitTime} day(s)</span>
            </div>
            {/* Shipment Status Section */}
            <div className="flex flex-col items-center justify-center p-2">
              <FontAwesomeIcon icon={shipmentStatus === 'Late' ? faExclamationTriangle : faCheckCircle} className={`text-3xl mb-1 ${shipmentStatus === 'Late' ? 'text-red-500' : 'text-green-500'}`} />
              <span className="text-lg font-bold text-gray-800">Status</span>
              <span className={`font-semibold ${shipmentStatus === 'Late' ? 'text-red-500' : 'text-green-500'}`}>
                {shipmentStatus}
              </span>
            </div>
            <div className="flex flex-col items-center justify-center p-2"> {/* Reduced padding */}
              <FontAwesomeIcon icon={faRoute} className="text-yellow-500 text-3xl mb-1" /> {/* Reduced margin */}
              <span className="text-lg font-bold text-gray-800 mb-1">Route</span>
              <div className="flex items-center justify-center">
                <span className="font-semibold">{shipment.consignorCity},</span>
                <FontAwesomeIcon icon={faArrowRight} className="mx-2 text-blue-500" />
                <span className="font-semibold">{shipment.consigneeCity},</span>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center p-2"> {/* Reduced padding */}
              <FontAwesomeIcon icon={faUserAlt} className="text-purple-500 text-3xl mb-1" /> {/* Reduced margin */}
              <span className="text-lg font-bold text-gray-800">Consignee</span>
              <span className="font-semibold">{shipment.consigneeName}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* First column for cards */}
          <div className="md:col-span-1 space-y-4">
            {/* Shipment Information Card */}
            <div className="bg-white shadow rounded-lg overflow-hidden mb-4">
              <div className="flex items-center px-5 py-3 bg-blue-900">
                <FontAwesomeIcon icon={faBoxes} className="text-blue-200 text-lg" />
                <h3 className="font-black text-lg ml-2 text-white">Shipment Information</h3>
              </div>
              <div className="p-5 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Mode</p>
                  <p className="text-base font-bold">{shipment.mode}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Goods Description</p>
                  <p className="text-base font-bold">{shipment.goodsDescription}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Transport</p>
                  <p className="text-base font-bold">{shipment.trans}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Service Level</p>
                  <p className="text-base font-bold">{shipment.serviceLevelCode}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Direction</p>
                  <p className="text-base font-bold">{shipment.direction}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">PPD/CCX</p>
                  <p className="text-base font-bold">{shipment.ppdCcx}</p>
                </div>
                {/* ... more details ... */}
              </div>
            </div>

            {/* Transport Details Card */}
            <div className="bg-white shadow rounded-lg overflow-hidden mb-4">
              <div className="flex items-center px-5 py-3 bg-blue-900">
                <FontAwesomeIcon icon={faShippingFast} className="text-blue-200 text-lg" />
                <h3 className="font-black text-lg ml-2 text-white">Transport Details</h3>
              </div>
              <div className="p-5 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Origin</p>
                  <p className="text-base font-bold">{shipment.origin}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Destination</p>
                  <p className="text-base font-bold">{shipment.destination}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Origin Region</p>
                  <p className="text-base font-bold">{shipment.originCountryRegion}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Destination Region</p>
                  <p className="text-base font-bold">{shipment.destinationCountryRegion}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">ETD</p>
                  <p className="text-base font-bold">{formatDate(shipment.etdFirstLoad)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">ETA</p>
                  <p className="text-base font-bold">{formatDate(shipment.etaLastDischarge)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Carrier</p>
                  <p className="text-base font-bold">{shipment.carrierName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Vessel</p>
                  <p className="text-base font-bold">{shipment.vessel}</p>
                </div>

                {/* ... more details ... */}
              </div>
            </div>

            {/* Consignment Details Card */}
            <div className="bg-white shadow rounded-lg overflow-hidden mb-4">
              <div className="flex items-center px-5 py-3 bg-blue-900">
                <FontAwesomeIcon icon={faUserAlt} className="text-blue-200 text-lg" />
                <h3 className="font-black text-lg ml-2 text-white">Consignment Details</h3>
              </div>
              <div className="p-5 grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Consignor</p>
                  <p className="text-base font-bold">{shipment.consignorName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Consignee</p>
                  <p className="text-base font-bold">{shipment.consigneeName}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Weight</p>
                  <p className="text-base font-bold">{shipment.weight}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Default UQ</p>
                  <p className="text-base font-bold">{shipment.defaultUq}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Volume</p>
                  <p className="text-base font-bold">{shipment.volume}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Loading Meters</p>
                  <p className="text-base font-bold">{shipment.loadingMeters}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Chargeable</p>
                  <p className="text-base font-bold">{shipment.chargeable}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Inner</p>
                  <p className="text-base font-bold">{shipment.inner}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Outer</p>
                  <p className="text-base font-bold">{shipment.outer}</p>
                </div>
              </div>
            </div>
          </div>
          {/* Second to Fourth columns for map */}
          <div className="md:col-span-3">
            {/* Shipment Route Title */}
            <div className="bg-blue-900 text-white px-5 py-3 shadow rounded-t-lg">
              <FontAwesomeIcon icon={faMapMarkedAlt} className="text-blue-200 text-lg" />
              <h2 className="font-black text-lg inline ml-2">Shipment Route</h2>
            </div>

            <div className="relative rounded-b-lg bg-white shadow">
              <HereMap
                apiKey={apiKey}
                consignor={{ consignorCity: shipment.consignorCity, consignorState: shipment.consignorState, consignorPostcode: shipment.consignorPostcode }}
                consignee={{ consigneeCity: shipment.consigneeCity, consigneeState: shipment.consigneeState, consigneePostcode: shipment.consigneePostcode }}
              />
            </div>
            <div className="bg-white shadow rounded-lg mt-6 p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-center">
                { /* Each date section with icon and divider */}
                <div className="border-r-2">
                  <FontAwesomeIcon icon={faTruckMoving} className="text-blue-500" />
                  <p className="text-sm font-semibold text-gray-600 mt-2">Origin ETD</p>
                  <hr className="my-2" />
                  <p className="text-base font-bold">{formatDate(shipment.originEtd)}</p>
                </div>
                <div className="border-r-2">
                  <FontAwesomeIcon icon={faPlaneArrival} className="text-green-500" />
                  <p className="text-sm font-semibold text-gray-600 mt-2">ETD First Load</p>
                  <hr className="my-2" />
                  <p className="text-base font-bold">{formatDate(shipment.etdFirstLoad)}</p>
                </div>
                <div className="border-r-2">
                  <FontAwesomeIcon icon={faShippingFast} className="text-red-500" />
                  <p className="text-sm font-semibold text-gray-600 mt-2">ETD Load</p>
                  <hr className="my-2" />
                  <p className="text-base font-bold">{formatDate(shipment.etdLoad)}</p>
                </div>
                <div className="border-r-2">
                  <FontAwesomeIcon icon={faDollarSign} className="text-yellow-500" />
                  <p className="text-sm font-semibold text-gray-600 mt-2">Consol ATD</p>
                  <hr className="my-2" />
                  <p className="text-base font-bold">{formatDate(shipment.consolAtd)}</p>
                </div>
                <div className="border-r-2">
                  <FontAwesomeIcon icon={faUserAlt} className="text-blue-800" />
                  <p className="text-sm font-semibold text-gray-600 mt-2">Destination ETA</p>
                  <hr className="my-2" />
                  <p className="text-base font-bold">{formatDate(shipment.destinationEta)}</p>
                </div>
                <div className="border-r-2">
                  <FontAwesomeIcon icon={faBoxes} className="text-orange-500" />
                  <p className="text-sm font-semibold text-gray-600 mt-2">ETA Last Discharge</p>
                  <hr className="my-2" />
                  <p className="text-base font-bold">{formatDate(shipment.etaLastDischarge)}</p>
                </div>
                <div>
                  <FontAwesomeIcon icon={faTruckMoving} className="text-pink-500" />
                  <p className="text-sm font-semibold text-gray-600 mt-2">ETA Discharge</p>
                  <hr className="my-2" />
                  <p className="text-base font-bold">{formatDate(shipment.etaDischarge)}</p>
                </div>

                <div>
                  <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-purple-500" />
                  <p className="text-sm font-semibold text-gray-600 mt-2">Consol ATA</p>
                  <hr className="my-2" />
                  <p className="text-base font-bold">{formatDate(shipment.consolAta)}</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </>
  );
};

export default ShipmentDetails;
